import { Avatar } from "@/components/ui/Avatar";
import { Icon } from "@/components/ui/Icon";
import { MarkdownInline } from "@/components/ui/MarkdownText";
import { memoUserToAvatar } from "@/lib/memoAvatar";
import type { OpenTask } from "@/lib/memoOpenTasks";
import type { MemoUser, UUID } from "@/lib/types";

/** `bis 20.07.` — the SAME shape the Frist pill on the entry itself uses. A second date
 *  format for the same deadline, two lines apart, reads as two different deadlines. */
function fmtDue(iso: string): string {
  const [, mo, d] = iso.split("-");
  return `${d}.${mo}.`;
}

/**
 * The Protokoll's open to-dos, listed in place under the header.
 *
 * 🔴 DELIBERATELY NOT a link to the SeriesPage "Aufgaben" tab. The pill counts to-dos in THIS
 * memo; that tab lists the WHOLE series via the linked board. Clicking "13 offene Aufgaben"
 * and landing on a list of forty is a lie about what you clicked — and in presentation it
 * would tear down the screen-share to go somewhere else entirely.
 *
 * What a room actually wants mid-meeting is "take me to them, one by one". So a row jumps to
 * its entry in the document you are already standing in, and flashes it.
 */
export function MemoOpenTasks({
  tasks,
  people,
  onJump,
}: {
  tasks: OpenTask[];
  /** Resolves an assignee id to a face. Absent ⇒ the row simply shows no avatar. */
  people: Map<UUID, MemoUser>;
  onJump: (entryId: UUID) => void;
}) {
  if (tasks.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 10,
        background: "var(--surface-soft)",
        overflow: "hidden",
      }}
    >
      {tasks.map((task, i) => {
        const person = task.assigneeId ? people.get(task.assigneeId) : undefined;
        return (
          <button
            key={task.entryId}
            type="button"
            onClick={() => onJump(task.entryId)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              textAlign: "left",
              padding: "9px 12px",
              background: "transparent",
              border: "none",
              borderTop: i === 0 ? "none" : "1px solid var(--hairline-soft)",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              color: "var(--ink-1)",
            }}
          >
            {person ? <Avatar user={memoUserToAvatar(person)} size={20} /> : null}
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {/* One line. The full text lives in the document the row jumps to — this is a
                  way IN, not a second copy of the Protokoll. */}
              <MarkdownInline text={task.text} />
            </span>
            {task.dueDate ? (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 12,
                  color: "var(--ink-3)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                bis {fmtDue(task.dueDate)}
              </span>
            ) : null}
            <Icon name="chevRight" size={14} color="var(--ink-4)" />
          </button>
        );
      })}
    </div>
  );
}
